import React, { useMemo, useState, useRef, useEffect } from "react";
import "./App.css";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Calendar from "./components/Calendar";
import ItemList from "./components/ItemList";
import PrivateLessonForm from "./components/PrivateLessonForm";
import ActivityForm from "./components/ActivityForm";
import { supabase } from "./supabase";
import { reportDataLoadError } from "./sentry";

// Import both map versions
import PickleMapHorizontal from "./images/PickleMap-Horizontal-New-2026.png";
import PickleMapVertical from "./images/PickleMap-Vertical-New-2026.png";

import PickleNewMapHorizontal from "./images/PickleMap-Horizontal-New-2026.png";
import PickleNewMapVertical from "./images/PickleMap-Vertical-New-2026.png";

// Import Font Awesome dependencies
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMap, faXmark, faTimes, faRotateRight } from "@fortawesome/free-solid-svg-icons";

const COURT_PREFIX_RE = /^(Tournament Court|Show Court|Padel Court) /;
const stripCourtPrefix = (name) => name.replace(COURT_PREFIX_RE, "");
const courtTypeFromName = (name) => name.match(COURT_PREFIX_RE)?.[1] || null;

// The "1. Sport" row keeps three kinds of button under the one `selectedSport`
// string: a court sport ("Pickle" / "Padel"), a coach flow ("<Sport> Coach") and
// an activities flow ("<Sport> Activities"). Only the court sports own courts, so
// the bookings preload/fetch effects below must treat the other two as "nothing
// to load", not as an invalid sport. Activities were a single hardcoded "Padel
// Activities" button until 2026-09-08; the suffix test lets one ActivityForm
// serve every sports row without another literal per sport.
const ACTIVITY_SPORT_SUFFIX = " Activities";
const isActivitySport = (s) => typeof s === "string" && s.endsWith(ACTIVITY_SPORT_SUFFIX);
// "Padel Activities" → "Padel": the sports.name the activities RPC filters on.
const activitySportName = (s) => (isActivitySport(s) ? s.slice(0, -ACTIVITY_SPORT_SUFFIX.length) : s);
const isCoachSport = (s) => typeof s === "string" && s.endsWith(" Coach");
const isCourtSport = (s) => s === "Pickle" || s === "Padel";

// --- Initial reference-data load: retry policy --------------------------------
// This load used to run exactly once on mount with no retry, so a single dropped
// request (a backgrounded phone tab, a lift-lobby moment) poisoned the whole
// session: `sports`/`courts`/`heldCourts` stayed empty until a manual full reload,
// while the page itself stayed interactive.
const DATA_LOAD_MAX_ATTEMPTS = 3;
const DATA_LOAD_BASE_DELAY_MS = 600;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// PostgREST answers an expired/invalid JWT with 401 (code PGRST301). Retrying that
// with the same dead token only burns the attempt budget — the session has to be
// refreshed first, so this class of failure is handled separately from a blip.
const isAuthError = (error) => {
  const status = error?.status ?? error?.context?.status ?? null;
  return (
    status === 401 ||
    error?.code === "PGRST301" ||
    /jwt|token/i.test(error?.message || "")
  );
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedSport, setSelectedSport] = useState(location.state?.selectedSport || null);
  const [selectedBookingType, setSelectedBookingType] = useState(
    location.state?.selectedBookingType || "Bookings"
  );
  const [selectedDate, setSelectedDate] = useState(location.state?.selectedDate || null);
  // INVARIANT: every key in selectedCourtTimeSlots belongs to selectedCourtType.
  // Not user-set — derived in handleCourtSelect from the clicked court's court_types.name.
  // It is the only thing that tells CourtCheckout which physical court to book and which
  // price band to charge, so never set selectedCourt without a matching selectedCourtType.
  const [selectedCourtType, setSelectedCourtType] = useState(location.state?.selectedCourtType || null);
  const [selectedCourt, setSelectedCourt] = useState(location.state?.selectedCourt || null);
  const [showMap, setShowMap] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(window.innerWidth <= 1024);
  const [visibleCourtTypeTooltip, setVisibleCourtTypeTooltip] = useState(null);
  // const [selectedTimeslots, setSelectedTimeslots] = useState(location.state?.selectedTimeslots || []);
  const [selectedCourtTimeSlots, setSelectedCourtTimeSlots] = useState(location.state?.selectedCourtTimeSlots || new Map());
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  // ✅ Getter (derived value)
  const selectedTimeslots = useMemo(() => {
    return selectedCourtTimeSlots.get(selectedCourt) || [];
  }, [selectedCourtTimeSlots, selectedCourt]);

// ✅ Setter
const setSelectedTimeslots = (newTimeslots) => {
  setSelectedCourtTimeSlots((prev) => {
    const updated = new Map(prev);
    updated.set(selectedCourt, newTimeslots);
    return updated;
  });
};
  const [bookedTimeslots, setBookedTimeslots] = useState([]);
  const [bookedTimeslotsByCourt, setBookedTimeslotsByCourt] = useState({});
  const [bookedTimeslotsCache, setBookedTimeslotsCache] = useState({});
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [heldCourts, setHeldCourts] = useState([]);
  const [notification, setNotification] = useState({ message: "", show: false });
  const [announcement, setAnnouncement] = useState({ message: "", show: false, dontShowAgain: false });

  const [sports, setSports] = useState([]);
  const [courtTypes, setCourtTypes] = useState([]);
  const [courts, setCourts] = useState([]);

  // Bumped by the modal's "Try Again" button to re-run the reference-data load
  // without forcing the customer to reload the whole page. `isLoadingReferenceData`
  // exists so that button can show progress — a retry can spend several seconds in
  // backoff, and with no feedback customers just hammer it.
  const [dataLoadAttempt, setDataLoadAttempt] = useState(0);
  const [isLoadingReferenceData, setIsLoadingReferenceData] = useState(true);

  const showCourtDescription = useMemo(
    () => courtTypes.find(ct => ct.name === "Show Court" && ct.sports?.name === "Pickle")?.description || "",
    [courtTypes]
  );

  const topRef = useRef(null);
  const sportSectionRef = useRef(null);
  const dateSectionRef = useRef(null);
  const courtSectionRef = useRef(null);
  const timeslotSectionRef = useRef(null);
  const checkoutButtonRef = useRef(null);
  const mapRef = useRef(null);
  const courtTypeRefs = useRef({});
  const timeoutRef = useRef(null);

  // The realtime bookings handler needs the LIVE selection, but must not be a dependency of the
  // subscription effect: selectedCourtType is now written on every court click, so listing it
  // there would tear down the channel and re-run the 10-day preload (blanking the court row
  // behind "Loading courts...") on the very click the user just made. Refs give freshness
  // without churn — and fix the pre-existing stale-selectedCourt closure at the same time.
  const selectedCourtRef = useRef(selectedCourt);
  const selectedCourtTypeRef = useRef(selectedCourtType);
  useEffect(() => {
    selectedCourtRef.current = selectedCourt;
    selectedCourtTypeRef.current = selectedCourtType;
  });

  // State for server time and booking window
  const [serverToday, setServerToday] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [maxBookingDate, setMaxBookingDate] = useState(null);

  // State for the cycle start date - Initialize to May 8, 2025
  const [cycleStartDate, setCycleStartDate] = useState(() => {
    const start = new Date(2025, 4, 8); // May 8, 2025
    start.setHours(0, 0, 0, 0);
    return start;
  });

  // Add useEffect to log re-renders safely
  useEffect(() => {
    console.log("App re-rendered with selectedCourt:", selectedCourt);
  });

  // Helper function to determine the "effective current date" based on 1:30 AM threshold
  const getEffectiveCurrentDate = (serverTime) => {
    const serverDate = new Date(serverTime);
    const oneThirtyAMToday = new Date(serverDate);
    oneThirtyAMToday.setHours(2, 0, 0, 0); // Set to 1:30 AM

    let effectiveDate;
    if (serverDate < oneThirtyAMToday) {
      effectiveDate = new Date(serverDate);
      effectiveDate.setDate(serverDate.getDate() - 1);
      effectiveDate.setHours(0, 0, 0, 0);
    } else {
      effectiveDate = new Date(serverDate);
      effectiveDate.setHours(0, 0, 0, 0);
    }
    return effectiveDate;
  };

  // Fetch server date and set initial booking window
  useEffect(() => {
    const fetchServerDate = async () => {
      try {
        const { data, error } = await supabase.rpc('get_current_time');
        if (error) throw error;

        const serverTime = new Date(data);
        const serverDateAtMidnight = new Date(serverTime);
        serverDateAtMidnight.setHours(0, 0, 0, 0);
        setServerToday(serverDateAtMidnight);

        const effectiveCurrentDate = getEffectiveCurrentDate(serverTime);
        setCurrentDate(effectiveCurrentDate);

        const newMaxDate = new Date(effectiveCurrentDate);
        if (userRole === "long_term_member"){
          //2025-10-13 Joe: long term member 14 days
          newMaxDate.setDate(effectiveCurrentDate.getDate() + 13);
        }else{
          //2025-10-13 Joe: non long term member 10 days
          newMaxDate.setDate(effectiveCurrentDate.getDate() + 9);
        }
        setMaxBookingDate(newMaxDate);

        const initialCycleStart = new Date(2025, 4, 8);
        initialCycleStart.setHours(0, 0, 0, 0);
        const daysSinceInitialCycleStart = (effectiveCurrentDate - initialCycleStart) / (1000 * 60 * 60 * 24);
        const cyclesPassed = Math.floor(daysSinceInitialCycleStart / 10);
        const newCycleStart = new Date(initialCycleStart);
        newCycleStart.setDate(initialCycleStart.getDate() + cyclesPassed * 10);
        setCycleStartDate(newCycleStart);
      } catch (error) {
        console.error("App.js: Error fetching server date:", error);
        const localTime = new Date();
        const localDateAtMidnight = new Date(localTime);
        localDateAtMidnight.setHours(0, 0, 0, 0);
        setServerToday(localDateAtMidnight);

        const effectiveCurrentDate = getEffectiveCurrentDate(localTime);
        setCurrentDate(effectiveCurrentDate);

        const newMaxDate = new Date(effectiveCurrentDate);
        if (userRole === "long_term_member"){
          //2025-10-13 Joe: long term member 14 days
          newMaxDate.setDate(effectiveCurrentDate.getDate() + 13);
        }else{
          //2025-10-13 Joe: non long term member 10 days
          newMaxDate.setDate(effectiveCurrentDate.getDate() + 9);
        }
        setMaxBookingDate(newMaxDate);

        const initialCycleStart = new Date(2025, 4, 8);
        initialCycleStart.setHours(0, 0, 0, 0);
        const daysSinceInitialCycleStart = (effectiveCurrentDate - initialCycleStart) / (1000 * 60 * 60 * 24);
        const cyclesPassed = Math.floor(daysSinceInitialCycleStart / 10);
        const newCycleStart = new Date(initialCycleStart);
        newCycleStart.setDate(initialCycleStart.getDate() + cyclesPassed * 10);
        setCycleStartDate(newCycleStart);
      }
    };

    fetchServerDate();
  }, [userRole]);

  // Update booking window daily based on serverToday
  useEffect(() => {
    if (!serverToday) return;

    const checkDateAndUpdate = () => {
      const now = new Date();
      const midnightToday = new Date(now);
      midnightToday.setHours(0, 0, 0, 0);
      const oneThirtyAMToday = new Date(midnightToday);
      oneThirtyAMToday.setHours(2, 0, 0, 0);
      const currentDateNormalized = new Date(currentDate);
      currentDateNormalized.setHours(0, 0, 0, 0);

      if (now >= oneThirtyAMToday && midnightToday.getTime() > currentDateNormalized.getTime()) {
        setCurrentDate(midnightToday);
        const newMaxDate = new Date(midnightToday);

        if (userRole === "long_term_member"){
          //2025-10-13 Joe: long term member 14 days
          newMaxDate.setDate(midnightToday.getDate() + 13);
        }else{
          //2025-10-13 Joe: non long term member 10 days
          newMaxDate.setDate(midnightToday.getDate() + 9);
        }

        setMaxBookingDate(newMaxDate);

        const daysSinceCycleStart = (midnightToday - cycleStartDate) / (1000 * 60 * 60 * 24);
        if (daysSinceCycleStart >= 10) {
          const newCycleStart = new Date(cycleStartDate);
          newCycleStart.setDate(newCycleStart.getDate() + 10);
          setCycleStartDate(newCycleStart);
        }
      }
    };

    const intervalId = setInterval(checkDateAndUpdate, 60 * 1000);
    checkDateAndUpdate();

    return () => clearInterval(intervalId);
  }, [serverToday, currentDate, cycleStartDate, userRole]);

  // Fetch the reference data the booking page is built from. All four reads are
  // issued together and applied ATOMICALLY — a pass either populates every piece of
  // state or none of it. That matters beyond tidiness: `heldCourts` is the sole
  // source of the priority-queue restrictions (see the isHeld checks below), and
  // nothing server-side re-checks them, so a pass that populated `courts` but left
  // `heldCourts` empty would render a fully bookable court grid with every hold
  // silently lifted.
  useEffect(() => {
    let cancelled = false;

    const steps = [
      {
        name: 'sports',
        query: () => supabase
          .from('sports')
          .select('id, name')
          .neq('name', 'Mixed')
          .order('name', { ascending: true }),
        apply: (data) => setSports(data || []),
      },
      {
        name: 'court_types',
        query: () => supabase
          .from('court_types')
          .select(`
            id,
            name,
            description,
            court_count,
            sport_id,
            sports (id, name)
          `)
          .order('name', { ascending: true }),
        apply: (data) => setCourtTypes(data || []),
      },
      {
        name: 'courts',
        query: () => supabase
          .from('courts')
          .select(`
            id,
            name,
            is_active,
            court_type_id,
            court_types (id, name, sport_id, sports (id, name))
          `)
          .order('name', { ascending: true }),
        apply: (data) => setCourts(data || []),
      },
      {
        name: 'held_courts',
        query: () => supabase
          .from('held_courts')
          .select(`
            court_id,
            queue_type,
            courts (id, name, court_type_id, court_types (id, name))
          `),
        apply: (data) => setHeldCourts(data || []),
      },
    ];

    // supabase-js resolves with { data, error } rather than rejecting, so surface the
    // first failing step as a throw and tag it with the step name for reporting.
    const runPass = async () => {
      const results = await Promise.all(steps.map((step) => step.query()));
      const failedIndex = results.findIndex((result) => result?.error);
      if (failedIndex !== -1) {
        const error = results[failedIndex].error;
        error.dataLoadStep = steps[failedIndex].name;
        throw error;
      }
      return results;
    };

    const loadReferenceData = async () => {
      let refreshedSession = false;

      for (let attempt = 1; attempt <= DATA_LOAD_MAX_ATTEMPTS; attempt++) {
        try {
          const results = await runPass();
          if (cancelled) return;
          results.forEach((result, index) => steps[index].apply(result.data));
          // Clear a stale failure modal if a later attempt recovered.
          setNotification((prev) => (prev.dataLoad ? { title: "", message: "", show: false } : prev));
          return;
        } catch (error) {
          if (cancelled) return;
          const step = error?.dataLoadStep || 'unknown';
          console.error(
            `Error fetching ${step} from Supabase (attempt ${attempt}/${DATA_LOAD_MAX_ATTEMPTS}):`,
            error
          );

          // An expired token never recovers by retrying — refresh once, then retry.
          // If the refresh itself fails the session is genuinely gone, so send them to
          // login rather than showing a booking page that cannot load anything.
          if (isAuthError(error)) {
            if (refreshedSession) {
              reportDataLoadError(step, error, { attempt, refreshedSession });
              navigate('/login', { state: { from: '/' } });
              return;
            }
            refreshedSession = true;
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (cancelled) return;
            if (refreshError) {
              reportDataLoadError(step, error, { attempt, refreshFailed: true });
              navigate('/login', { state: { from: '/' } });
              return;
            }
            continue;
          }

          if (attempt === DATA_LOAD_MAX_ATTEMPTS) {
            reportDataLoadError(step, error, { attempt });
            setNotification({
              title: 'Couldn’t Load Booking Data',
              message:
                'We couldn’t load the courts and schedule — this is usually a connection problem. Please check your internet and try again.',
              show: true,
              dataLoad: true,
            });
            return;
          }

          // Exponential backoff with jitter, so a brief blip recovers on its own.
          await sleep(DATA_LOAD_BASE_DELAY_MS * 2 ** (attempt - 1) + Math.random() * 250);
          if (cancelled) return;
        }
      }
    };

    setIsLoadingReferenceData(true);
    loadReferenceData().finally(() => {
      if (!cancelled) setIsLoadingReferenceData(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoadAttempt]);

  // Preload booking data and set up real-time subscription
  useEffect(() => {
    const preloadBookings = async () => {
      if (!selectedSport || !courts.length || !currentDate || !maxBookingDate) return;

      setIsLoadingBookings(true);

      try {
        // Coach and Activities selections have no courts to preload — an expected
        // early exit, not an invalid sport (this used to console.error on every
        // coach/activity click).
        if (!isCourtSport(selectedSport)) {
          return;
        }

        const relevantCourts = courts.filter(c =>
          c.court_types &&
          c.court_types.sports &&
          c.court_types.sports.name === selectedSport
        );

        if (relevantCourts.length === 0) {
          console.error("App.js: No courts found for preloading:", { selectedSport });
          setBookedTimeslotsCache({});
          setIsLoadingBookings(false);
          return;
        }

        const courtIds = relevantCourts.map(c => c.id);

        const dates = [];
        const startDate = new Date(currentDate);
        const endDate = new Date(maxBookingDate);
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          dates.push(`${year}-${month}-${day}`);
        }

        // One request covers the whole booking window; fetching per-date here
        // fires ~10 parallel queries on every page load (N+1).
        const { data: windowBookings, error } = await supabase
          .from('bookings')
          .select('timeslot, court_id, booking_date')
          .in('court_id', courtIds)
          .in('booking_date', dates);

        if (error) {
          console.error("App.js: Error preloading bookings:", error);
        }

        const bookingsByDate = {};
        dates.forEach(date => { bookingsByDate[date] = []; });
        (windowBookings || []).forEach(booking => {
          if (bookingsByDate[booking.booking_date]) {
            bookingsByDate[booking.booking_date].push(booking);
          }
        });

        const newCache = {};
        dates.forEach(bookingDate => {
          const bookedMap = {};
          bookingsByDate[bookingDate].forEach(booking => {
            if (booking.timeslot === "02:00 AM") return;
            if (!bookedMap[booking.court_id]) bookedMap[booking.court_id] = new Set();
            bookedMap[booking.court_id].add(booking.timeslot);
          });

          const bookedTimeslotsByCourt = {};
          relevantCourts.forEach(court => {
            bookedTimeslotsByCourt[court.name] = Array.from(bookedMap[court.id] || new Set());
          });

          newCache[bookingDate] = bookedTimeslotsByCourt;
        });

        setBookedTimeslotsCache(newCache);

        if (selectedDate) {
          const year = selectedDate.getFullYear();
          const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
          const day = String(selectedDate.getDate()).padStart(2, '0');
          const bookingDate = `${year}-${month}-${day}`;
          setBookedTimeslotsByCourt(newCache[bookingDate] || {});
        }
      } catch (error) {
        console.error("App.js: Unexpected error preloading bookings:", error);
      } finally {
        setIsLoadingBookings(false);
      }
    };

    preloadBookings();

    let subscription = null;
    if (selectedSport && courts.length && currentDate && maxBookingDate) {
      const relevantCourts = courts.filter(c =>
        c.court_types &&
        c.court_types.sports &&
        c.court_types.sports.name === selectedSport
      );

      if (relevantCourts.length > 0) {
        const courtIds = relevantCourts.map(c => c.id);
        const dates = [];
        const startDate = new Date(currentDate);
        const endDate = new Date(maxBookingDate);
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          dates.push(`${year}-${month}-${day}`);
        }

        const handleBookingChange = (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          const bookingDate = newRecord?.booking_date || oldRecord?.booking_date;
          const courtId = newRecord?.court_id || oldRecord?.court_id;
          const newTimeslot = newRecord?.timeslot;
          const oldTimeslot = oldRecord?.timeslot;

          if (!dates.includes(bookingDate) || !courtIds.includes(courtId)) return;
          if (newTimeslot === "02:00 AM" || oldTimeslot === "02:00 AM") return;

          setBookedTimeslotsCache(prevCache => {
            const updatedCache = { ...prevCache };
            if (!updatedCache[bookingDate]) {
              updatedCache[bookingDate] = {};
              relevantCourts.forEach(court => {
                updatedCache[bookingDate][court.name] = [];
              });
            }

            const court = relevantCourts.find(c => c.id === courtId);
            if (!court) return updatedCache;

            let currentTimeslots = new Set(updatedCache[bookingDate][court.name] || []);

            if (eventType === 'INSERT') {
              if (newTimeslot) currentTimeslots.add(newTimeslot);
            } else if (eventType === 'UPDATE') {
              if (oldTimeslot && oldTimeslot !== newTimeslot) {
                currentTimeslots.delete(oldTimeslot);
              }
              if (newTimeslot) currentTimeslots.add(newTimeslot);
            } else if (eventType === 'DELETE') {
              if (oldTimeslot) currentTimeslots.delete(oldTimeslot);
            }

            updatedCache[bookingDate][court.name] = Array.from(currentTimeslots);

            if (selectedDate) {
              const year = selectedDate.getFullYear();
              const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
              const day = String(selectedDate.getDate()).padStart(2, '0');
              const selectedBookingDate = `${year}-${month}-${day}`;
              if (bookingDate === selectedBookingDate) {
                setBookedTimeslotsByCourt(updatedCache[bookingDate]);
              }
            }

            // Read through refs, never the closure: this handler is created once per
            // subscription and the user switches courts without re-subscribing.
            if (
              selectedCourtRef.current &&
              stripCourtPrefix(court.name) === selectedCourtRef.current &&
              courtTypeFromName(court.name) ===
                (selectedSport === "Padel" ? "Padel Court" : selectedCourtTypeRef.current)
            ) {
              setBookedTimeslots(Array.from(currentTimeslots));
            }

            return updatedCache;
          });
        };

        subscription = supabase
          .channel('bookings-changes')
          .on('postgres_changes', 
            { event: '*', schema: 'public', table: 'bookings', filter: `court_id=in.(${courtIds.join(',')})` }, 
            handleBookingChange
          )
          .subscribe((status, err) => {
            if (status === 'SUBSCRIBED') {
              console.log('App.js: Real-time subscription established for bookings');
            } else if (status === 'CHANNEL_ERROR') {
              console.error('App.js: Channel error in bookings subscription:', err?.message);
            } else if (status === 'TIMED_OUT') {
              console.error('App.js: Real-time bookings subscription timed out');
            } else if (status === 'CLOSED') {
              console.log('App.js: Real-time bookings subscription closed');
            }
          });
      }
    }

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
        console.log('App.js: Unsubscribed from bookings-changes channel');
      }
    };
    // selectedCourtType is deliberately NOT a dependency: it is read through a ref above, and
    // including it would re-preload + re-subscribe on the first court click of every date.
  }, [selectedSport, courts, currentDate, maxBookingDate, selectedDate]);

  const minDate = currentDate;
  // Coaching requires 24h lead time, so the earliest selectable coach date is tomorrow
  // (otherwise today shows a fully-disabled slot grid). Court bookings keep same-day.
  const coachMinDate = currentDate
    ? (() => { const d = new Date(currentDate); d.setDate(d.getDate() + 1); return d; })()
    : currentDate;

  useEffect(() => {
    const checkSessionAndFetchUserRole = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("Error checking session:", sessionError);
        navigate("/login");
        return;
      }
      if (!session) {
        navigate("/login", { state: { from: location.pathname, ...location.state } });
        return;
      }

      const userId = session.user.id;
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('membership_status')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        console.error("Error fetching user profile:", profileError);
        setUserRole('normal_member');
        return;
      }

      setUserRole(profile.membership_status || 'normal_member');
    };

    checkSessionAndFetchUserRole();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || (!session && event === "INITIAL_SESSION")) {
        navigate("/login", { state: { from: location.pathname, ...location.state } });
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate, location.pathname, location.state]);

  useEffect(() => {
    if (location.state) {
      setSelectedSport(location.state.selectedSport || null);
      setSelectedBookingType(location.state.selectedBookingType || "Bookings");
      setSelectedDate(location.state.selectedDate || null);
      setSelectedCourtType(location.state.selectedCourtType || null);
      setSelectedCourt(location.state.selectedCourt || null);
      // setSelectedTimeslots(location.state.selectedTimeslots || []);
      setSelectedCourtTimeSlots(new Map());
    }
  }, [location.state]);

  useEffect(() => {
    setSelectedDate(null);
  }, [selectedSport]);

  useEffect(() => {
    let timeoutId;
    const fetchBookedTimeslots = async () => {
      console.log("fetchBookedTimeslots called");
      if (!selectedSport || !selectedDate || !courts.length) {
        setBookedTimeslots([]);
        setBookedTimeslotsByCourt({});
        return;
      }
      // Coach / Activities flows pick a date on the shared calendar too, but they
      // book through their own forms — no court timeslots to fetch, and the
      // "No courts found" error below is only meaningful for a court sport.
      if (!isCourtSport(selectedSport)) {
        setBookedTimeslots([]);
        setBookedTimeslotsByCourt({});
        return;
      }

      let courtPrefix;
      const relevantCourts = courts.filter(c =>
        c.court_types &&
        c.court_types.sports &&
        c.court_types.sports.name === selectedSport
      );

      if (relevantCourts.length === 0) {
        console.error("App.js: No courts found for:", { selectedSport, selectedCourtType });
        setBookedTimeslots([]);
        setBookedTimeslotsByCourt({});
        return;
      }

      if (selectedSport === "Padel") {
        courtPrefix = "Padel Court";
      } else if (selectedSport === "Pickle") {
        courtPrefix = selectedCourtType;
      } else {
        console.error("App.js: Invalid sport selected:", selectedSport);
        setBookedTimeslots([]);
        setBookedTimeslotsByCourt({});
        return;
      }

      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const bookingDate = `${year}-${month}-${day}`;

      let bookedMap = {};

      if (bookedTimeslotsCache[bookingDate]) {
        console.log("Using cached data for", bookingDate);
        setBookedTimeslotsByCourt(bookedTimeslotsCache[bookingDate]);
        relevantCourts.forEach(court => {
          bookedMap[court.id] = new Set(bookedTimeslotsCache[bookingDate][court.name] || []);
        });
      } else {
        console.log("Setting isLoadingBookings to true");
        setIsLoadingBookings(true);
        try {
          const courtIds = relevantCourts.map(c => c.id);
          const { data: bookings, error } = await supabase
            .from('bookings')
            .select('timeslot, court_id')
            .in('court_id', courtIds)
            .eq('booking_date', bookingDate);

          if (error) {
            console.error("App.js: Error fetching booked timeslots:", error);
            setNotification({ message: "Failed to fetch booked timeslots. Please try again.", show: true });
            setIsLoadingBookings(false);
            return;
          }

          bookings.forEach(booking => {
            if (booking.timeslot === "02:00 AM") return;
            if (!bookedMap[booking.court_id]) bookedMap[booking.court_id] = new Set();
            bookedMap[booking.court_id].add(booking.timeslot);
          });

          const bookedTimeslotsByCourt = {};
          relevantCourts.forEach(court => {
            bookedTimeslotsByCourt[court.name] = Array.from(bookedMap[court.id] || new Set());
          });

          setBookedTimeslotsByCourt(bookedTimeslotsByCourt);
          setBookedTimeslotsCache(prev => ({
            ...prev,
            [bookingDate]: bookedTimeslotsByCourt,
          }));
        } catch (error) {
          console.error("App.js: Unexpected error fetching booked timeslots:", error);
          setNotification({ message: "An unexpected error occurred while fetching timeslots.", show: true });
        } finally {
          setIsLoadingBookings(false);
        }
      }

      if (selectedCourt && relevantCourts) {
        const courtName = `${courtPrefix} ${selectedCourt}`;
        const court = relevantCourts.find(c => c.name === courtName);
        if (court) {
          const heldCourt = heldCourts.find(hc => hc.court_id === court.id);
          const isHeld = !!heldCourt;
          const queueType = heldCourt?.queue_type || null;

          const allTimeslots = [
            '06:00 AM', '07:00 AM', '08:00 AM',
            '09:00 AM', '10:00 AM', '11:00 AM',
            '12:00 PM', '01:00 PM', '02:00 PM',
            '03:00 PM', '04:00 PM', '05:00 PM',
            '06:00 PM', '07:00 PM', '08:00 PM',
            '09:00 PM', '10:00 PM', '11:00 PM',
            '12:00 AM', '01:00 AM'
          ];

          let allBookedTimeslots = Array.from(bookedMap[court.id] || new Set());

          if (isHeld) {
            const cycleStartDateNormalized = new Date(cycleStartDate);
            cycleStartDateNormalized.setHours(0, 0, 0, 0);
            const selectedDateNormalized = new Date(selectedDate);
            selectedDateNormalized.setHours(0, 0, 0, 0);
            const cycleDiff = (selectedDateNormalized - cycleStartDateNormalized) / (1000 * 60 * 60 * 24);
            const dayInCycle = (Math.floor(cycleDiff) % 10) + 1;

            let restrictedTimeslots = [];
            if (dayInCycle >= 1 && dayInCycle <= 3) {
              // Days 1-3: No restrictions
            } else if (dayInCycle >= 4 && dayInCycle <= 10) {
              const priorityRoles = ['coach', 'super_host', 'long_term_member'];

              if (queueType === 'super_priority') {
                if (dayInCycle >= 4 && dayInCycle <= 10) {
                  if (userRole !== 'athlete') {
                    restrictedTimeslots = allTimeslots;
                  }
                }
              } else if (queueType === 'priority') {
                if (dayInCycle >= 6 && dayInCycle <= 10) {
                  if (!priorityRoles.includes(userRole)) {
                    restrictedTimeslots = allTimeslots;
                  }
                }
              }
            }

            restrictedTimeslots.forEach(ts => allBookedTimeslots.push(ts));
          }

          setBookedTimeslots([...new Set(allBookedTimeslots)]);
        } else {
          setBookedTimeslots([]);
        }
      }
    };

    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fetchBookedTimeslots();
    }, 100);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedSport, selectedDate, courts, selectedCourtType]);

  // Update bookedTimeslots when selectedCourt changes, using cached data
  useEffect(() => {
    if (!selectedCourt || !selectedDate || !courts.length) {
      setBookedTimeslots([]);
      return;
    }

    const relevantCourts = courts.filter(c =>
      c.court_types &&
      c.court_types.sports &&
      c.court_types.sports.name === selectedSport
    );

    if (relevantCourts.length === 0) return;

    let courtPrefix;
    if (selectedSport === "Padel") {
      courtPrefix = "Padel Court";
    } else if (selectedSport === "Pickle") {
      courtPrefix = selectedCourtType;
    }

    const courtName = `${courtPrefix} ${selectedCourt}`;
    const court = relevantCourts.find(c => c.name === courtName);
    if (!court) {
      setBookedTimeslots([]);
      return;
    }

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const bookingDate = `${year}-${month}-${day}`;

    const cachedData = bookedTimeslotsCache[bookingDate];
    if (cachedData) {
      const timeslots = new Set(cachedData[court.name] || []);
      const heldCourt = heldCourts.find(hc => hc.court_id === court.id);
      const isHeld = !!heldCourt;
      const queueType = heldCourt?.queue_type || null;

      const allTimeslots = [
        '06:00 AM', '07:00 AM', '08:00 AM',
        '09:00 AM', '10:00 AM', '11:00 AM',
        '12:00 PM', '01:00 PM', '02:00 PM',
        '03:00 PM', '04:00 PM', '05:00 PM',
        '06:00 PM', '07:00 PM', '08:00 PM',
        '09:00 PM', '10:00 PM', '11:00 PM',
        '12:00 AM', '01:00 AM'
      ];

      let allBookedTimeslots = Array.from(timeslots);

      if (isHeld) {
        const cycleStartDateNormalized = new Date(cycleStartDate);
        cycleStartDateNormalized.setHours(0, 0, 0, 0);
        const selectedDateNormalized = new Date(selectedDate);
        selectedDateNormalized.setHours(0, 0, 0, 0);
        const cycleDiff = (selectedDateNormalized - cycleStartDateNormalized) / (1000 * 60 * 60 * 24);
        const dayInCycle = (Math.floor(cycleDiff) % 10) + 1;

        let restrictedTimeslots = [];
        if (dayInCycle >= 1 && dayInCycle <= 3) {
          // No restrictions
        } else if (dayInCycle >= 4 && dayInCycle <= 10) {
          const priorityRoles = ['coach', 'super_host', 'long_term_member'];

          if (queueType === 'super_priority') {
            if (dayInCycle >= 4 && dayInCycle <= 10) {
              if (userRole !== 'athlete') {
                restrictedTimeslots = allTimeslots;
              }
            }
          } else if (queueType === 'priority') {
            if (dayInCycle >= 6 && dayInCycle <= 10) {
              if (!priorityRoles.includes(userRole)) {
                restrictedTimeslots = allTimeslots;
              }
            }
          }
        }

        restrictedTimeslots.forEach(ts => allBookedTimeslots.push(ts));
      }

      setBookedTimeslots([...new Set(allBookedTimeslots)]);
    } else {
      setBookedTimeslots([]);
    }
  }, [selectedCourt, selectedSport, selectedDate, courts, selectedCourtType, heldCourts, userRole, cycleStartDate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 480) {
      if (showMap) {
        topRef.current?.scrollIntoView({ behavior: "smooth" });
        return;
      }

      if (selectedSport && sportSectionRef.current && !selectedDate) {
        dateSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (selectedDate && dateSectionRef.current && !selectedCourt) {
        courtSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (selectedCourt && courtSectionRef.current && /*selectedTimeslots.length === 0*/ selectedCourtTimeSlots.size === 0) {
        timeslotSectionRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [selectedSport, selectedDate, selectedCourt, showMap]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (visibleCourtTypeTooltip !== null) {
        const courtTypeRef = courtTypeRefs.current[visibleCourtTypeTooltip];
        if (courtTypeRef && !courtTypeRef.contains(event.target)) {
          setVisibleCourtTypeTooltip(null);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visibleCourtTypeTooltip]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  console.log("isMobileOrTablet:",isMobileOrTablet);
  const currentMap = isMobileOrTablet ? PickleMapVertical : PickleMapHorizontal;
  const currentNewMap = isMobileOrTablet ? PickleNewMapVertical : PickleNewMapHorizontal;

  const handleSportSelect = (sport) => {
    setSelectedSport((prevSport) => {
      if (prevSport === sport) {
        setSelectedDate(null);
        setSelectedCourtType(null);
        setSelectedCourt(null);
        // setSelectedTimeslots([]);
        setSelectedCourtTimeSlots(new Map());
        setSelectedAddOns([]);
        return null;
      }
      return sport;
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate((prevDate) => {
      if (prevDate && date && prevDate.getTime() === date.getTime()) {
        setSelectedCourtType(null);
        setSelectedCourt(null);
        // setSelectedTimeslots([]);
        setSelectedCourtTimeSlots(new Map());
        setSelectedAddOns([]);
        return null;
      }
      setSelectedCourtType(null);
      setSelectedCourt(null);
      // setSelectedTimeslots([]);
      setSelectedCourtTimeSlots(new Map());
      setSelectedAddOns([]);
      return date || null;
    });
  };

  const showCourtTypeTooltip = (courtType, e) => {
    e.stopPropagation();

    if (window.innerWidth <= 480) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setVisibleCourtTypeTooltip(courtType);

    timeoutRef.current = setTimeout(() => {
      setVisibleCourtTypeTooltip(null);
    }, 2000);
  };

  // One order = one court type: Show Court and Tournament Court are priced differently and
  // a single selectedCourtType is applied to every entry of selectedCourtTimeSlots downstream.
  // Returns false when the click was rejected, so the caller can skip any click-triggered UI.
  const handleCourtSelect = (court) => {
    const isDeselect = selectedCourt === court.name && selectedCourtType === court.courtType;

    if (!isDeselect && selectedCourtTimeSlots.size > 0 && selectedCourtType && court.courtType !== selectedCourtType) {
      const courtsWithSlots = Array.from(selectedCourtTimeSlots.keys())
        .map(courtKey => `${selectedCourtType} ${courtKey}`)
        .join(", ");
      setNotification({
        title: "One Court Type Per Booking",
        message: `${selectedCourtType} and ${court.courtType} are priced differently and must be booked as separate orders. You currently have timeslots selected on ${courtsWithSlots} — either continue to checkout with those, or re-select the date to start over before choosing ${court.courtType}.`,
        show: true,
      });
      return false;
    }

    // Deselecting keeps selectedCourtType: the timeslot map may still hold that type's slots.
    setSelectedCourtType(court.courtType);
    setSelectedCourt(isDeselect ? null : court.name);
    return true;
  };

  const handleTimeslotSelect = (timeslot, e) => {
    if (e) {
      e.preventDefault();
    }
    
    setSelectedCourtTimeSlots((prev) => {
      let newMap = new Map(prev);
      let existingTimeslots = newMap.get(selectedCourt) || [];
      if (existingTimeslots.includes(timeslot)) {
        existingTimeslots = existingTimeslots.filter((t) => t !== timeslot);
      } else {
        existingTimeslots = [...existingTimeslots, timeslot];
      }

      if (existingTimeslots.length === 0){
        newMap.delete(selectedCourt);
      }else{
        newMap.set(selectedCourt, existingTimeslots);
      }

      return newMap;
    });
    // setSelectedTimeslots((prev) =>
    //   prev.includes(timeslot) ? prev.filter((t) => t !== timeslot) : [...prev, timeslot]
    // );
  };

  const handleBookingTypeSelect = (type) => {
    setSelectedBookingType(type);
    setSelectedSport(null);
    setSelectedDate(null);
    setSelectedCourtType(null);
    setSelectedCourt(null);
    // setSelectedTimeslots([]);
    setSelectedCourtTimeSlots(new Map());
    setSelectedAddOns([]);
  };

  const handleProfileClick = () => {
    setSelectedBookingType(null);
    setSelectedSport(null);
    setSelectedDate(null);
    setSelectedCourtType(null);
    setSelectedCourt(null);
    // setSelectedTimeslots([]);
    setSelectedCourtTimeSlots(new Map());
    setSelectedAddOns([]);
    navigate("/profile");
  };

  const handleShowMap = () => {
    setShowMap(true);
  };

  const handleCloseMap = () => {
    setShowMap(false);
  };

  const handleCloseNotification = () => {
    setNotification({ title: "", message: "", show: false });
  };

  // Re-run the reference-data load in place. The modal stays open until the retry
  // either succeeds (which clears it) or exhausts its attempts (which rewrites it).
  const handleRetryDataLoad = () => {
    setDataLoadAttempt((n) => n + 1);
  };

  const handleCloseAnnouncement = () => {
    if (announcement.dontShowAgain) {
      localStorage.setItem('hideAnnouncement-2026-01-01', 'true');
    }
    setAnnouncement({ message: "", show: false, dontShowAgain: false });
  };

  const handleDontShowAgainChange = (e) => {
    setAnnouncement(prev => ({ ...prev, dontShowAgain: e.target.checked }));
  };

  const getAvailableCourts = () => {
    if (!selectedSport || !selectedDate) return [];

    const filteredCourts = courts.filter(
      court => court.court_types && court.court_types.sports && court.court_types.sports.name === selectedSport
    );

    const allTimeslots = [
      '06:00 AM', '07:00 AM', '08:00 AM',
      '09:00 AM', '10:00 AM', '11:00 AM',
      '12:00 PM', '01:00 PM', '02:00 PM',
      '03:00 PM', '04:00 PM', '05:00 PM',
      '06:00 PM', '07:00 PM', '08:00 PM',
      '09:00 PM', '10:00 PM', '11:00 PM',
      '12:00 AM', '01:00 AM'
    ];

    const cycleStartDateNormalized = new Date(cycleStartDate);
    cycleStartDateNormalized.setHours(0, 0, 0, 0);
    const selectedDateNormalized = new Date(selectedDate);
    selectedDateNormalized.setHours(0, 0, 0, 0);
    const cycleDiff = (selectedDateNormalized - cycleStartDateNormalized) / (1000 * 60 * 60 * 24);
    const dayInCycle = (Math.floor(cycleDiff) % 10) + 1;

    return filteredCourts
      .map(court => {
        const match = court.name.match(/\d+$/);
        const number = match ? parseInt(match[0], 10) : 0;
        const courtType = court.court_types.name;
        const isShowCourt = courtType === "Show Court";
        const courtName = stripCourtPrefix(court.name);
        const isFullyBooked = bookedTimeslotsByCourt[court.name]?.length === allTimeslots.length;

        let isRestricted = !court.is_active;
        const heldCourt = heldCourts.find(hc => hc.court_id === court.id);
        const isHeld = !!heldCourt;
        const queueType = heldCourt?.queue_type || null;

        // if (court.name == "Padel Court 4"){
        //   isRestricted = true;
        // }else{
          if (isHeld) {
            if (dayInCycle >= 1 && dayInCycle <= 3) {
              // No restrictions
            } else if (dayInCycle >= 4 && dayInCycle <= 10) {
              const priorityRoles = ['coach', 'super_host', 'long_term_member'];

              if (queueType === 'super_priority') {
                if (dayInCycle >= 4 && dayInCycle <= 10) {
                  if (userRole !== 'athlete') {
                    isRestricted = true;
                  }
                }
              } else if (queueType === 'priority') {
                if (dayInCycle >= 6 && dayInCycle <= 10) {
                  if (!priorityRoles.includes(userRole)) {
                    isRestricted = true;
                  }
                }
              }
            }
          }
        // }

        return {
          name: courtName,
          fullName: court.name,
          courtType,
          isShowCourt,
          label: isShowCourt ? "Show Court" : courtName,
          number,
          isFullyBooked,
          isRestricted,
        };
      })
      .sort((a, b) => (a.isShowCourt === b.isShowCourt ? a.number - b.number : (a.isShowCourt ? 1 : -1)));
  };

  const handleCheckout = async () => {

    // if (selectedTimeslots.length === 0) {
    if (selectedCourtTimeSlots.size == 0) {
      setNotification({ message: "Please select at least one timeslot before proceeding to checkout.", show: true });
      return;
    }

    try {
      if (userRole === 'normal_member') {
        const user = await supabase.auth.getUser();
        const userId = user.data.user.id;

        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const bookingDate = `${year}-${month}-${day}`;

        const { count, error } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('booking_date', bookingDate);

        if (error) {
          console.error("App.js: Error fetching booking count:", error);
          setNotification({ message: "Failed to verify booking limit. Please try again or contact support.", show: true });
          return;
        }

        const existingBookings = count || 0;
        // const newBookings = selectedCourtTimeSlots.size;
        let newBookings = 0;
        for (const timeslots of selectedCourtTimeSlots.values()) {
          newBookings += timeslots.length;
        }
        const totalBookings = existingBookings + newBookings;

        if (totalBookings > 10) {
          setNotification({ message: 'As a normal member, you can only book up to 10 slots per day. Please select a different date or contact support for assistance.', show: true });
          return;
        }
      }

      console.log("selected court timeslots:", selectedCourtTimeSlots);

      // Navigate to add-ons page
      navigate("/add-ons", {
        state: {
          selectedSport,
          selectedCourtType,
          selectedDate,
          selectedCourtTimeSlots,
          userRole,
        },
      });
    } catch (error) {
      console.error("App.js: Checkout error:", error);
      setNotification({ message: "An unexpected error occurred during checkout. Please try again or contact support.", show: true });
    }
  };

  // Show announcement on page load if not dismissed
  useEffect(() => {
    const hideAnnouncement = localStorage.getItem('hideAnnouncement-2026-01-01');
    if (!hideAnnouncement) {
      setAnnouncement({
        message: `
          <h3>New Changes and Announcements</h3>
          <ul>
            <li>
              Effective for booking 1st January 2026 onwards

              <div class="map-comparison">
                <div class="map-column">
                  <h4>New</h4>
                  <img
                    src="${currentNewMap}"
                    alt="New Facility Map"
                    class="map-image clickable"
                    onclick="document.getElementById('newMapDialog').showModal()"
                  />
                </div>
              </div>

            </li>
          </ul>

          <!-- New Map Dialog -->
          <dialog id="newMapDialog" class="map-dialog">
            <img src="${currentNewMap}" alt="New Facility Map Fullscreen" />
            <button onclick="this.closest('dialog').close()">Close</button>
          </dialog>
        `,
        show: true,
        dontShowAgain: false,
      });
    }
  }, []);

  return (
    <div className="App">
      <Navbar
        selectedSport={selectedSport}
        selectedBookingType={selectedBookingType}
        onBookingTypeSelect={handleBookingTypeSelect}
        onProfileClick={handleProfileClick}
      />
      <div ref={topRef}></div>
      <div className="content">
        {selectedBookingType === "Bookings" && (
          <div className="court-booking-content">
            {showMap ? (
              <div className="map-container">
                <FontAwesomeIcon
                  icon={faXmark}
                  className="close-map-icon"
                  onClick={handleCloseMap}
                />
                <img
                  ref={mapRef}
                  src={currentMap}
                  alt="Facility Map"
                  className={`map-image ${isMobileOrTablet ? "vertical" : "horizontal"}`}
                />
              </div>
            ) : (
              <>
                <div className="selection-panel">
                  <>
                    <h2 className="step-title" ref={sportSectionRef}>
                      1. Sport
                    </h2>
                    <div className="category-buttons">
                      {sports.some(s => s.name === "Pickle") && (
                        <button
                          onClick={() => handleSportSelect("Pickle")}
                          className={`category-button ${selectedSport === "Pickle" ? "active" : ""}`}
                        >
                          Pickle
                        </button>
                      )}
                      {/* Each Activities button sits beside its court sport and is gated on
                          the same sports row: get_open_activities filters on sports.name, so
                          a sport that isn't in the table can't have activities to list. */}
                      {sports.some(s => s.name === "Pickle") && (
                        <button
                          onClick={() => handleSportSelect("Pickle Activities")}
                          className={`category-button ${selectedSport === "Pickle Activities" ? "active" : ""}`}
                        >
                          Pickle Activities
                        </button>
                      )}
                      {sports.some(s => s.name === "Padel") && (
                        <button
                          onClick={() => handleSportSelect("Padel")}
                          className={`category-button ${selectedSport === "Padel" ? "active" : ""}`}
                        >
                          Padel
                        </button>
                      )}
                      {sports.some(s => s.name === "Padel") && (
                        <button
                          onClick={() => handleSportSelect("Padel Activities")}
                          className={`category-button ${selectedSport === "Padel Activities" ? "active" : ""}`}
                        >
                          Padel Activities
                        </button>
                      )}
                      <button
                        onClick={() => handleSportSelect("Padel Coach")}
                        className={`category-button ${selectedSport === "Padel Coach" ? "active" : ""}`}
                      >
                        Padel Coach
                      </button>
                      <button
                        onClick={() => handleSportSelect("Pickle Coach")}
                        className={`category-button ${selectedSport === "Pickle Coach" ? "active" : ""}`}
                      >
                        Pickle Coach
                      </button>
                    </div>

                    {selectedSport && currentDate && maxBookingDate && (
                      <div className="date-selection">
                        <h2 className="step-title" ref={dateSectionRef}>
                          2. Date
                        </h2>
                        <Calendar
                          selectedDate={selectedDate}
                          onDateSelect={handleDateSelect}
                          minDate={isCoachSport(selectedSport) ? coachMinDate : minDate}
                          maxDate={maxBookingDate}
                          selectedSport={selectedSport}
                          disabledDays={[]}
                          autoSelectFirst={!isActivitySport(selectedSport)}
                        />
                      </div>
                    )}
                  </>
                </div>

                {isActivitySport(selectedSport) && (
                  <div className="activities-content">
                    <ActivityForm
                      sport={activitySportName(selectedSport)}
                      externalSelectedDate={selectedDate}
                    />
                  </div>
                )}

                {selectedSport === "Padel Coach" && selectedDate && (
                  <PrivateLessonForm
                    selectedSport="Padel"
                    handleSportSelect={() => {}}
                    sports={sports}
                    serverToday={serverToday}
                    currentDate={currentDate}
                    maxBookingDate={maxBookingDate}
                    cycleStartDate={cycleStartDate}
                    skipSportSelection={true}
                    externalSelectedDate={selectedDate}
                  />
                )}

                {selectedSport === "Pickle Coach" && selectedDate && (
                  <PrivateLessonForm
                    selectedSport="Pickle"
                    handleSportSelect={() => {}}
                    sports={sports}
                    serverToday={serverToday}
                    currentDate={currentDate}
                    maxBookingDate={maxBookingDate}
                    cycleStartDate={cycleStartDate}
                    skipSportSelection={true}
                    externalSelectedDate={selectedDate}
                  />
                )}

                {!isCoachSport(selectedSport) && !isActivitySport(selectedSport) && selectedDate && (
                  <div className="confirm-booking">
                    {(selectedSport === "Padel" || selectedSport === "Pickle") && (
                      <>
                        <h2 className="step-title" ref={courtSectionRef}>
                          3. Court
                        </h2>
                        {/* The map button stays mounted while bookings load — it does not depend
                            on availability, and Pickle rendered it unconditionally before. */}
                        <div className="court-options">
                          {isLoadingBookings ? (
                            <div>Loading courts...</div>
                          ) : (
                            getAvailableCourts().map((court) => {
                              const disabled = court.isFullyBooked || court.isRestricted;
                              const isSelected = selectedCourt === court.name && selectedCourtType === court.courtType;
                              const button = (
                                <button
                                  className={`court-options ${isSelected ? "selected" : ""} ${disabled ? "fully-booked" : ""}`}
                                  onClick={(e) => {
                                    if (disabled) return;
                                    const accepted = handleCourtSelect(court);
                                    if (accepted && court.isShowCourt && window.innerWidth > 480) {
                                      showCourtTypeTooltip("Show Court", e);
                                    }
                                  }}
                                  disabled={disabled}
                                  aria-label={court.fullName}
                                >
                                  {court.label}
                                </button>
                              );

                              return court.isShowCourt ? (
                                <div
                                  className="tooltip"
                                  key={court.fullName}
                                  ref={(el) => (courtTypeRefs.current["Show Court"] = el)}
                                >
                                  {button}
                                  <span
                                    className={`tooltiptext ${
                                      visibleCourtTypeTooltip === "Show Court" ? "visible" : ""
                                    }`}
                                  >
                                    Show Court{showCourtDescription ? `: ${showCourtDescription}` : ""}
                                  </span>
                                </div>
                              ) : (
                                <React.Fragment key={court.fullName}>{button}</React.Fragment>
                              );
                            })
                          )}
                          <button className="show-map-button court-options" onClick={handleShowMap}>
                            <FontAwesomeIcon icon={faMap} />
                          </button>
                        </div>
                      </>
                    )}

                    {selectedCourt && (
                      <>
                        <h2 className="step-title" ref={timeslotSectionRef}>
                          4. Timeslot
                        </h2>
                        <ItemList
                          selectedSport={selectedSport}
                          selectedBookingType={selectedBookingType}
                          selectedDate={selectedDate}
                          selectedCourtType={selectedCourtType}
                          selectedCourt={selectedCourt}
                          selectedTimeslots={selectedTimeslots}
                          onTimeslotSelect={handleTimeslotSelect}
                          bookedTimeslots={bookedTimeslots}
                          userRole={userRole}
                          heldCourts={heldCourts}
                        />
                        <button
                          className="submit-button"
                          ref={checkoutButtonRef}
                          onClick={handleCheckout}
                        >
                          Continue
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>

      {notification.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{notification.title || "Checkout Error"}</h2>
            <p>{notification.message}</p>
            {notification.dataLoad && (
              <button
                className="modal-close-button"
                onClick={handleRetryDataLoad}
                disabled={isLoadingReferenceData}
              >
                <FontAwesomeIcon
                  icon={faRotateRight}
                  spin={isLoadingReferenceData}
                />{" "}
                {isLoadingReferenceData ? "Retrying…" : "Try Again"}
              </button>
            )}
            <button
              className="modal-close-button"
              onClick={handleCloseNotification}
            >
              <FontAwesomeIcon icon={faTimes} /> Got It
            </button>
          </div>
        </div>
      )}

      {announcement.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Announcements</h2>
            <div dangerouslySetInnerHTML={{ __html: announcement.message }} />
            <div className="modal-actions">
              <label>
                <input
                  type="checkbox"
                  checked={announcement.dontShowAgain}
                  onChange={handleDontShowAgainChange}
                /> Don't show again
              </label>
              <button
                className="modal-close-button"
                onClick={handleCloseAnnouncement}
              >
                <FontAwesomeIcon icon={faTimes} /> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
